<template>
  <div class="mb-4">
    <div>
      <v-row class="mx-0" justify="center" align="center">
        <v-text-field
          id="Stats_obj_search"
          :label="$tc('object_stats_btn_name')"
          outlined
          single-line
          clearable
          v-model="objectId"
          @click:clear="clearView()"
          :error-messages="objectErrors"
          @input="$v.objectId.$touch()"
          @blur="$v.objectId.$touch()"
          @keydown.delete="deleteKeyPressed"
        />

        <v-col class="mb-8" cols="2">
          <v-select
            hide-details=""
            height="55"
            :items="refreshIntervalOptions"
            item-text="name"
            :label="$t('scale')"
            v-model="duration"
            outlined
          />
        </v-col>

        <v-btn
          v-if="!isStarted"
          id="Stats_obj_btn_start"
          class="mb-8"
          height="55"
          color="secondary"
          :disabled="$v.$invalid"
          :dark="!$v.$invalid"
          @click="execute()"
        >
          <v-icon>
            play_arrow
          </v-icon>
        </v-btn>

        <v-btn
          v-else
          id="Stats_obj_btn_stop"
          class="mb-8"
          height="55"
          color="secondary"
          :disabled="$v.$invalid"
          :dark="!$v.$invalid"
          @click="handlePause()"
        >
          <v-icon>
            pause
          </v-icon>
        </v-btn>
      </v-row>
    </div>

    <div v-if="!loading">
      <v-card outlined class="rounded-lg mb-4">
        <v-card-text>
          <v-row no-gutters justify="center" align="center">
            <v-col class="text-center" cols="12" sm="4">
              <div id="object_stats_start_s" class="mb-1 my-sm-0">
                {{ `${$t("object_stats_start_s")}:` }} {{ `${$getDateString({ date: wctStats, timeDetailLevel: 2 }) || '--/--/----'}` }}
              </div>
            </v-col>

            <v-divider v-if="$vuetify.breakpoint.smAndUp" vertical />

            <v-col class="text-center" cols="12" sm="4">
              <div id="object_stats_last_s" class="mb-1 my-sm-0">
                {{ `${$t("object_stats_last_s")}: ` }} {{ `${$getDateString({ date: dateLastStats, timeDetailLevel: 2 }) || '--/--/----'}` }}
              </div>
            </v-col>

            <v-divider v-if="$vuetify.breakpoint.smAndUp" vertical />

            <v-col class="text-center" cols="12" sm="4">
              <div class="mb-1 my-sm-0">
                {{ `${$t("object_count")}: ${countStats}` }}
              </div>
            </v-col>

            <!-- <v-divider v-if="$vuetify.breakpoint.smAndUp" vertical />

            <v-col class="text-center" cols="12" sm="3">
              {{ `${$t("now")}: ` }}  {{ `${$getDateString({ date: date, timeDetailLevel: 2 }) || '--/--/----'}` }}
            </v-col> -->
          </v-row>
        </v-card-text>
      </v-card>

      <v-row>
        <!-- COUNT STATS -->
        <v-col :cols="cardColunmWidth">
          <v-card outlined class="rounded-lg" height="370">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              {{ $t("object_count") }}
            </v-card-title>

            <v-card-text>
              <count-stats-chart
                :is-started="isStarted"
                :dataset="countStatsDataset"
                :scala="duration"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <!-- RATE STATS -->
        <v-col :cols="cardColunmWidth">
          <v-card outlined class="rounded-lg" height="370">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              {{ $t("object_rate_s") }}
            </v-card-title>

            <v-card-text>
              <rate-stats-chart
                :is-started="isStarted"
                :dataset="rateStatsDataset"
                :scala="duration"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <!-- STATS RATE VARIATION -->
        <v-col :cols="cardColunmWidth">
          <v-card outlined class="rounded-lg" height="370">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              {{ $t("object_variation_rate_s") }}
            </v-card-title>

            <v-card-text>
              <usage-rate-variation
                :is-started="isStarted"
                :dataset="statsUsageRateVariation"
                :scala="duration"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <div ref="elementToScrollTo" />

        <v-col :cols="cardColunmWidth">
          <v-card outlined class="rounded-lg" height="370">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              {{ $t("average_usage_last_seconds") }}
            </v-card-title>

            <v-card-text>
              <average-count-stats-chart
                :is-started="isStarted"
                :five-secsdataset="statsCountIn5secsList"
                :ten-secsdataset="statsCountIn10secsList"
                :thirty-secsdataset="statsCountIn30secsList"
                :sixty-secsdataset="statsCountIn60secsList"
                :scala="duration"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <!-- STATS EFFECTIVE RATE -->
        <v-col :cols="cardColunmWidth">
          <v-card outlined class="rounded-lg" height="370">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              {{ $t("effective_rate_s") }}
            </v-card-title>

            <v-card-text>
              <effective-rate-stats-chart
                :is-started="isStarted"
                :dataset="effectiveRateStatsDataset"
                :scala="duration"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <!-- VARIATION STATS EFFECTIVE RATE -->
        <v-col :cols="cardColunmWidth">
          <v-card outlined class="rounded-lg" height="370">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              {{ $t("variation_effective_rate_s") }}
            </v-card-title>

            <v-card-text>
              <variation-effective-rate-stats-chart
                :is-started="isStarted"
                :dataset="variationEffectiveRateStatsDataset"
                :scala="duration"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <div v-else>
      {{ $tc("loading") }}
      <v-progress-linear indeterminate class="mb-0" />
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'

import CountStatsChart from './UsageCharts/CountStatsChart.vue'
import RateStatsChart from './UsageCharts/RateStatsChart.vue'
import UsageRateVariation from './UsageCharts/UsageRateVariation.vue'
import EffectiveRateStatsChart from './UsageCharts/EffectiveRateStatsChart.vue'
import VariationEffectiveRateStatsChart from './UsageCharts/VariationEffectiveRateStatsChart.vue'
import AverageCountStatsChart from './UsageCharts/AverageCountStatsChart.vue'

export default {
  components: {
    CountStatsChart,
    RateStatsChart,
    UsageRateVariation,
    EffectiveRateStatsChart,
    VariationEffectiveRateStatsChart,
    AverageCountStatsChart
  },
  created () {
    this.loadKeys()
  },
  mounted () {
    window.addEventListener('beforeunload', () => {
      window.scrollTo(0, 0)
    })

    this.setIntervalId = setInterval(() => {
      if (this.startRequisition) {
        this.getStatsByObjectId()
      }
    }, this.requisitionInterval)
  },
  data () {
    return {
      setIntervalId: null,
      objects: null,
      objectStatistic: {},
      objectId: '',
      loading: false,
      isStarted: false,
      startRequisition: false,
      date: 0,

      statsColor: '#5ad45a',
      transactionColor: '#0bb4ff',

      dateLastStats: 0,
      countStats: 0,
      wctStats: 0,
      countStatsDataset: [],
      rateStatsDataset: [],
      rateDeltaStatsList: [],
      effectiveRateStatsDataset: [],
      effectiveRateDeltaStatsList: [],
      variationEffectiveRateStatsDataset: [],
      usageRateVariationStats: [],
      statsUsageRateVariation: [],
      statsCountIn5secsList: [],
      statsCountIn10secsList: [],
      statsCountIn30secsList: [],
      statsCountIn60secsList: [],
      countStatsList: [],

      cardColorList: ['#F3F3F2', '#404040'],
      duration: 15000
    }
  },
  computed: {
    refreshIntervalOptions () {
      return [
        {
          name: this.$tc('n_second', 2, { n: 15 }),
          value: 15 * 1000
        },
        {
          name: this.$tc('minute', 1),
          value: 1 * 60 * 1000
        },
        {
          name: this.$tc('minute', 2, { n: 5 }),
          value: 5 * 60 * 1000
        },
        {
          name: this.$tc('hour', 1),
          value: 60 * 60 * 1000
        },
        {
          name: this.$tc('hour', 2, { n: 3 }),
          value: 3 * 60 * 60 * 1000
        },
        {
          name: this.$tc('hour', 2, { n: 6 }),
          value: 6 * 60 * 60 * 1000
        },
        {
          name: this.$tc('hour', 2, { n: 24 }),
          value: 24 * 60 * 60 * 1000
        }
      ]
    },
    requisitionInterval () {
      return this.duration / 75
    },
    cardColunmWidth () {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          return 12
        case 'sm':
          return 12
        case 'md':
          return 6
        case 'lg':
        case 'xl':
          return 4
        default:
          return 4
      }
    },
    objectErrors () {
      const errors = []
      if (!this.$v.objectId.$dirty) return errors
      !this.$v.objectId.required &&
        errors.push(this.$t('$validation.required'))
      !this.$v.objectId.isExistingObject &&
        errors.push(this.$t('$validation.key_name_not_found'))
      !this.$v.objectId.isSingleObject &&
        errors.push(this.$t('$validation.is_not_single_key'))
      return errors
    },
    darkMode () {
      return this.$store.state.ui.darkModeEnabled
    },
    cardColor () {
      return this.darkMode ? this.cardColorList[1] : this.cardColorList[0]
    }
  },
  methods: {
    loadKeys () {
      this.$store.dispatch('keys/getListKeys').then((res) => {
        this.objects = res
      })
    },
    getStatsByObjectId () {
      const body = { obj: this.objectId }

      this.$store
        .dispatch('hsm/getObjectStatistic', {
          body
        })
        .then((res) => {
          const seconds = Math.round(new Date().getTime() / 1000)
          this.countStatsList.push({ seconds, count: res.count_s })
          if (this.countStatsList.length > 100000) {
            this.countStatsList.shift()
          }

          this.mountStatsDataSet(res)
        })
        .catch((err) => {
          this.startRequisition = false
          this.isStarted = false
          clearInterval(this.setIntervalId)

          const errObj = this.$store.getters['error/getErrorObj'](err)
          if (errObj) {
            this.$notify.hsmError(errObj, err)
          } else {
            this.$notify.error({ message: err })
          }
        })
    },
    mountStatsDataSet (observability) {
      const now = new Date()
      this.date = now

      const startStats = observability.start_s / 1000000 // Convert difference from microseconds to seconds
      const lastStats = observability.last_s / 1000000 // Convert difference from microseconds to seconds
      const countStats = observability.count_s
      const swctStats = observability.swct_s

      const wct = new Date(swctStats * 1000)

      const lastTouch = this.calculateLastTouch(startStats, lastStats, swctStats)

      const intervalBetweenWtcAndNow = (now - wct) / 1000

      if (this.isStarted === true) {
        this.dateLastStats = lastTouch
        this.countStats = countStats
        this.wctStats = swctStats
      }

      this.countStatsDataset.push({ x: now, y: countStats })

      if (this.countStatsDataset.length > 100000) {
        this.countStatsDataset.shift()
      }

      const rateStats = countStats / intervalBetweenWtcAndNow

      this.rateStatsDataset.push({ x: now, y: rateStats.toFixed(2) })

      if (this.rateStatsDataset.length > 100000) {
        this.rateStatsDataset.shift()
      }

      this.rateDeltaStatsList.push({
        rate: rateStats,
        interval: intervalBetweenWtcAndNow
      })

      if (this.rateDeltaStatsList.length > 100000) {
        this.rateDeltaStatsList.shift()
      }

      if (this.rateDeltaStatsList.length >= 2) {
        const rateSubList = this.rateDeltaStatsList.slice(-2)
        const deltaRate = rateSubList[1].rate - rateSubList[0].rate
        const deltaInterval = rateSubList[1].interval - rateSubList[0].interval
        const variation = deltaRate / deltaInterval
        this.statsUsageRateVariation.push({ x: now, y: variation.toFixed(2) })

        if (this.statsUsageRateVariation.length > 100000) {
          this.statsUsageRateVariation.shift()
        }
      }

      const lastStartDiff = lastStats - startStats
      const effectiveRate = countStats / lastStartDiff

      this.effectiveRateStatsDataset.push({ x: now, y: effectiveRate })
      if (this.effectiveRateStatsDataset.length > 100000) {
        this.effectiveRateStatsDataset.shift()
      }

      this.effectiveRateDeltaStatsList.push({
        rate: effectiveRate,
        interval: intervalBetweenWtcAndNow
      })

      if (this.effectiveRateDeltaStatsList.length > 100000) {
        this.effectiveRateDeltaStatsList.shift()
      }

      if (this.effectiveRateDeltaStatsList.length >= 2) {
        const rateSubList = this.effectiveRateDeltaStatsList.slice(-2)
        const deltaRate = rateSubList[1].rate - rateSubList[0].rate
        const deltaInterval = rateSubList[1].interval - rateSubList[0].interval
        const variation = deltaRate / deltaInterval
        this.variationEffectiveRateStatsDataset.push({ x: now, y: variation })

        if (this.variationEffectiveRateStatsDataset.length > 100000) {
          this.variationEffectiveRateStatsDataset.shift()
        }
      }

      const seconds = Math.round(new Date().getTime() / 1000)
      const firstInterval = 5
      const secondInterval = 10
      const thirdInterval = 30
      const fourthInterval = 60

      // First Interval
      const statsAverage5secs = this.statsIntervalAverageCalculator(countStats, seconds, firstInterval)
      this.statsCountIn5secsList.push({ x: this.date, y: statsAverage5secs })

      // Second Interval
      const statsAverage10secs = this.statsIntervalAverageCalculator(countStats, seconds, secondInterval)
      this.statsCountIn10secsList.push({ x: this.date, y: statsAverage10secs })

      // Third Interval
      const statsAverage30secs = this.statsIntervalAverageCalculator(countStats, seconds, thirdInterval)
      this.statsCountIn30secsList.push({ x: this.date, y: statsAverage30secs })

      // Fourth Interval
      const statsAverage60secs = this.statsIntervalAverageCalculator(countStats, seconds, fourthInterval)
      this.statsCountIn60secsList.push({ x: this.date, y: statsAverage60secs })
    },
    statsIntervalAverageCalculator (currentCount, currentSeconds, interval) {
      const counterPassedSeconds = this.countStatsList.filter(item => {
        return item.seconds === currentSeconds - interval
      }).slice(-1)[0]?.count

      return (currentCount - counterPassedSeconds) / interval
    },
    calculateLastTouch (start, last, swct) {
      const diff = last - start
      const lastUse = swct + diff
      return new Date(lastUse * 1000)
    },
    execute () {
      this.scrollToElement()
      this.loading = true
      this.getStatsByObjectId()
      this.loading = false
      this.isStarted = true
      this.startRequisition = true
    },
    handlePause () {
      this.isStarted = false
    },
    clearView () {
      this.isStarted = false
      this.startRequisition = false
      this.objectId = ''
      this.objectStatistic = {}
      this.date = 0

      this.dateLastStats = 0
      this.countStats = 0
      this.wctStats = 0
      this.countStatsDataset = []
      this.rateStatsDataset = []
      this.rateDeltaStatsList = []
      this.effectiveRateStatsDataset = []
      this.effectiveRateDeltaStatsList = []
      this.variationEffectiveRateStatsDataset = []
      this.usageRateVariationStats = []
      this.statsUsageRateVariation = []
      this.statsCountIn5secsList = []
      this.statsCountIn10secsList = []
      this.statsCountIn30secsList = []
      this.statsCountIn60secsList = []
      this.countStatsList = []
    },
    deleteKeyPressed () {
      // mesmo que o clearView, porém sem resetar objectId
      this.isStarted = false
      this.startRequisition = false
      this.objectStatistic = {}
      this.date = 0

      this.dateLastStats = 0
      this.countStats = 0
      this.wctStats = 0
      this.countStatsDataset = []
      this.rateStatsDataset = []
      this.rateDeltaStatsList = []
      this.effectiveRateStatsDataset = []
      this.effectiveRateDeltaStatsList = []
      this.variationEffectiveRateStatsDataset = []
      this.usageRateVariationStats = []
      this.statsUsageRateVariation = []
      this.statsCountIn5secsList = []
      this.statsCountIn10secsList = []
      this.statsCountIn30secsList = []
      this.statsCountIn60secsList = []
      this.countStatsList = []
    },
    scrollToElement () {
      const targetElement = this.$refs.elementToScrollTo
      const topScroll = targetElement.offsetTop + 50
      window.scrollTo({
        top: topScroll,
        behavior: 'smooth'
      })
    }
  },
  validations () {
    this.loadKeys()
    const validation = {}

    const isExistingObject = (value) => {
      if (this.objects !== null) {
        let filteredObjects = 0
        if (value !== null) {
          filteredObjects = this.objects.filter(
            (object) => object.indexOf(value) > -1
          ).length
        }
        return filteredObjects > 0
      } else {
        return false
      }
    }

    const isSingleObject = (value) => {
      if (this.objects !== null) {
        let filteredObjects = 0
        if (value !== null) {
          filteredObjects = this.objects.filter(
            (object) => object.indexOf(value) > -1 && value === object
          ).length
        }
        return filteredObjects === 1
      } else {
        return false
      }
    }

    validation.objectId = {
      required,
      isExistingObject,
      isSingleObject
    }

    return validation
  },
  destroyed () {
    this.clearView()
    clearInterval(this.setIntervalId)
  }
}
</script>
